
import { defineComponent } from "vue";
import DrawerColumns from "@/components/drawers/bottom/DrawerColumns.vue";
import FloodRisks from "@/components/drawers/bottom/FloodRisks.vue";
import FloodClimateChangeRisks from "@/components/drawers/bottom/FloodClimateChangeRisks.vue";
import {
  BottomTab,
  GDT,
  IJBACombinedRisk,
  IJbaRiskSummary,
  IJbaScoreCount,
  RiskLookupType,
} from "@/types";
import AccumulationsTabs from "@/components/accumulations/AccumulationsTabs.vue";
import TableDataViewer from "@/components/drawers/bottom/TableDataViewer.vue";
import Spinner from "@/components/shared/Spinner.vue";
import RiskEntry from "@/components/shared/RiskEntry.vue";
import {
  useDataSource,
  useGammaData,
  useGammaWebServices,
  useLayout,
  useMap,
  useOrgSettings,
} from "@/services";
import { mapState } from "pinia";
import ClimateRisksChart from "./ClimateRisksChart.vue";

export default defineComponent({
  name: "BottomDrawer",
  components: {
    TableDataViewer,
    DrawerColumns,
    FloodRisks,
    FloodClimateChangeRisks,
    AccumulationsTabs,
    Spinner,
    RiskEntry,
    ClimateRisksChart,
  },
  data: () => ({
    isDragging: false,
    tableDefinition: null,
    currentTab: 0,
    pagination: {
      totalRows: 0,
      perPage: 25,
    },
  }),
  computed: {
    ...mapState(useLayout, [
      "drawerHeight",
      "isBottomDrawerOpen",
      "isRightSidebarOpen",
      "bottomActiveTab",
    ]),
    ...mapState(useMap, ["dataBoundary"]),
    ...mapState(useGammaData, [
      "hasDataSources",
      "useDatasetRecordGroups",
      "activeSourceId",
    ]),
    ...mapState(useGammaWebServices, [
      "otherRisks",
      "isLoadingRisks",
      "selectedOtherRisk",
      "selectedRisk",
    ]),
    ...mapState(useOrgSettings, [
      "showRags",
      "showBottomAccumulation",
      "applicationSettings",
      "showRiskDetails",
    ]),
    selectedOtherRiskJBASummary(): IJbaRiskSummary | undefined {
      return this.selectedOtherRisk
        ? (useGammaWebServices().selectedOtherRisk?.data
            .RiskSummary as IJbaRiskSummary)
        : undefined;
    },
    riskLookupChart(): any | undefined {
      return useOrgSettings().riskLookups.find(
        (x) => x.type === RiskLookupType.FloodClimateChangeChart
      );
    },
    jbaScoreCounts(): IJbaScoreCount[] {
      return useGammaWebServices().selectedRisk?.scoreCounts || [];
    },
    floodClimateChangeScores(): IJBACombinedRisk | undefined {
      return (
        useGammaWebServices().selectedRisk?.data?.RiskSummary as
          | IJbaRiskSummary
          | undefined
      )?.JBACombinedRisk;
    },
    tabs(): BottomTab[] {
      const tabs: BottomTab[] = [];

      if (this.showRiskDetails) {
        tabs.push(BottomTab.RiskDetails);
      }

      if (this.hasDataSources) {
        if (this.showBottomAccumulation) {
          tabs.push(BottomTab.Accumulations);
        }

        tabs.push(BottomTab.Query);

        if (this.useDatasetRecordGroups) {
          tabs.push(BottomTab.Groups);
        }
      }

      return tabs;
    },
    tabIndex: {
      get(): number {
        return this.tabs.findIndex((x) => x === this.bottomActiveTab);
      },
      set(val: number) {
        useLayout().setBottomActiveTab(this.tabs[val]);
      },
    },
    activeTab(): BottomTab {
      return this.bottomActiveTab || this.tabs[0];
    },
    activeFilters(): GDT.DataFilter[] {
      return this.activeSourceId
        ? useDataSource(this.activeSourceId)().userFilters
        : [];
    },
    drawerHeight: {
      get(): number {
        return useLayout().drawerHeight;
      },
      set(val: number) {
        useLayout().drawerH = val;
      },
    },
    isVisible: {
      get(): boolean {
        return useLayout().isBottomDrawerOpen;
      },
      set(value: boolean) {
        useLayout().isBottomDrawerOpen = value;
      },
    },
    risksSubTitles(): string[] {
      return [
        `${this.selectedRisk?.name} Details`,
        "",
        this.selectedOtherRisk?.name
          ? `${this.selectedOtherRisk?.name} Details`
          : "",
      ];
    },
  },
  watch: {
    drawerHeight(val) {
      if (this.$el instanceof HTMLDivElement) {
        this.$el.style.height = val + "px";
      }
    },
    activeTab(val: number) {
      if (val === 2 && this.isRightSidebarOpen) {
        useLayout().rightSidebarIndex = 1;
      }
    },
  },
  mounted() {
    if (this.$el instanceof HTMLDivElement) {
      this.drawerHeight = this.$el?.offsetHeight;
    }
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("resize", this.resetColumns);
  },
  beforeDestroy() {
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("resize", this.resetColumns);
  },
  methods: {
    onMouseDown(e: MouseEvent) {
      e.preventDefault();
      this.isDragging = true;
    },
    onMouseUp() {
      this.isDragging = false;
    },
    onMouseMove(e: MouseEvent) {
      if (this.isDragging) {
        useLayout().drawerH = window.outerHeight - e.clientY - 120;
      }
    },
    getDescription(name?: string): string {
      return (
        useOrgSettings().riskLookups.find((x) => x.name === name)
          ?.description || ""
      );
    },
    resetColumns() {
      if (this.$el instanceof HTMLDivElement) {
        [...(this.$el.querySelectorAll(".ps") || [])].forEach((el) => {
          const container = el.closest(".drawer-container");
          if (container instanceof HTMLDivElement) {
            container.removeAttribute("style");
          }
        });
        this.drawerHeight = this.$el.offsetHeight;
      }
    },
    onTabClick(bottomTab: BottomTab) {
      if (bottomTab === this.bottomActiveTab) {
        this.toggleDrawer();
      } else {
        useLayout().setBottomActiveTab(bottomTab, true);
      }
    },
    toggleDrawer() {
      this.isVisible = !this.isVisible;
    },
    openChart() {
      this.$bvModal.show("risk-chart-modal");
    },
  },
});
